import React, { useState, useRef, useCallback, useContext } from "react";
import AsyncSelect from "react-select/async";
import makeAnimated from "react-select/animated";
import { FailedNotification } from "./Notifications";
import { ToastContext } from "../hooks/ToastContext";

const animatedComponents = makeAnimated();

function EntityChips2({
  label,
  isRequired = false,
  placeholder = "Select / Search entities",
  emptyRecordMsg = "No entities available",
  customCloseIcon = null,
  styleOverrides = {},
  fetchData,
  selectedEntities,
  setSelectedEntities,
  displayKey = "name",
  onEntityAdd,
  onEntityRemove,
  enableCopyPaste = false, // New prop to enable/disable copy-pasting
}) {
  const { handleToastState } = useContext(ToastContext);

  const showToast = () => {
    handleToastState(true); // Set toast as active
    // Logic to display the toast
  };

  const dismissToast = () => {
    handleToastState(false); // Set toast as inactive
    // Logic to dismiss the toast
  };
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const selectRef = useRef(null);
  const inputRef = useRef(null);

  // Load options for normal search
  const loadOptions = useCallback(
    async (inputValue) => {
      console.log("[Search] loadOptions called with:", inputValue);
      setError(null);
      setLoading(true);

      try {
        const data = await fetchData(inputValue, 1);
        console.log("[Search] API Response:", data);
        return data.items || [];
      } catch (error) {
        console.error("[Search] Error:", error);
        setError("Error fetching suggestions");
        return [];
      } finally {
        setLoading(false);
      }
    },
    [fetchData]
  );

  // Handle selection
  const handleSelect = useCallback(
    (selected) => {
      console.log("[Selection] handleSelect called");

      if (!selected) {
        setSelectedEntities([]);
      } else {
        if (onEntityAdd && selected.length > (selectedEntities?.length || 0)) {
          const newItem = selected[selected.length - 1];
          onEntityAdd(newItem);
        } else if (
          onEntityRemove &&
          selected.length < (selectedEntities?.length || 0)
        ) {
          const removedItem = selectedEntities.find(
            (item) => !selected.some((s) => s.id === item.id)
          );
          onEntityRemove(removedItem);
        }
        setSelectedEntities(selected);
      }

      // Schedule focus for next tick
      requestAnimationFrame(() => {
        console.log("[Selection] Attempting focus in RAF");
        selectRef.current?.focus();
      });
    },
    [selectedEntities, setSelectedEntities, onEntityAdd, onEntityRemove]
  );

  // Custom input with paste handling
  const CustomInput = useCallback(
    ({ value, onChange, onFocus, ...props }) => {
      console.log("[CustomInput] Rendering with props:", props);

      // Handle delete button click
      const handleDelete = () => {
        // Remove the last selected entity
        const lastIndex = selectedEntities.length - 1;
        selectedEntities.splice(lastIndex, 1);

        // Blur the input field and restore focus
        selectRef.current.blur();
        // Set focus on the input field after deletion
        selectRef.current.focus();
      };

      const handleKeyDown = (event) => {
        if (event.key === "Delete" || event.key === "Backspace") {
          handleDelete();
        }
      };

      const handlePaste = async (event) => {
        // Check if copy-paste is enabled
        if (!enableCopyPaste) {
          FailedNotification(
            "Copy-paste functionality is disabled.",
            showToast,
            dismissToast
          );

          return; // Terminate the function early if copy-paste is disabled
        }

        event.preventDefault();
        console.log("[Paste] Event triggered");

        const pastedText = event.clipboardData.getData("text/plain");
        console.log("[Paste] Raw text:", pastedText);

        // Clean up the pasted text - remove quotes and split
        const pastedValues = pastedText
          .replace(/['"]/g, "") // Remove all quotes
          .split(/[\n,]+/) // Split on commas/newlines
          .map((v) => v.trim())
          .filter(Boolean);

        console.log("[Paste] Processed values:", pastedValues);

        if (!pastedValues.length) {
          console.log("[Paste] No valid values found");
          return;
        }

        setLoading(true);
        setError(null);

        try {
          let notFoundValues = [];

          // Search for each value in the API
          const searchResults = await Promise.all(
            pastedValues.map((item) => fetchData(item, 1))
          );

          searchResults.forEach((result, index) => {
            if (result.items.length > 0) {
              const matchedItem = result.items[0]; // Access the item property from the result

              // Check if the value is already selected
              const existingIndex = selectedEntities.findIndex(
                (item) => item.id === matchedItem.id
              );

              // Add the value only if it's not already selected
              if (existingIndex === -1) {
                selectedEntities.push(matchedItem);
                onEntityAdd(matchedItem);
              }
            } else {
              notFoundValues.push(pastedValues[index]);
            }
          });

          // // Update the selectedEntities array and trigger the callback function after the loop finishes
          // setSelectedEntities([...selectedEntities]);
          // onEntityAdd?.();

          // Show error for values not found
          if (notFoundValues.length > 0) {
            setError(`  Not found - ${notFoundValues.join(", ")}`);
          }
        } catch (error) {
          console.error("[Paste] Error:", error);
          setError("Error processing pasted values");
        } finally {
          setLoading(false);
          // // Clear the input safely
          // // Clear the input value after processing the pasted values
          // if (onChange) {
          //   onChange("");
          // }
        }
      };

      const handleFocus = (e) => {
        console.log("[CustomInput] Input focused");
        onFocus?.(e);
      };

      return (
        <div style={{ flex: 1, minWidth: 0 }}>
          <input
            {...props}
            ref={inputRef}
            // value={value}
            // onChange={(e) => onChange?.(e.target.value)}
            onChange={onChange} // Uncomment to enable input search
            onPaste={handlePaste}
            onKeyDown={handleKeyDown}
            placeholder={selectedEntities.length > 0 ? "" : placeholder}
            onFocus={handleFocus}
            // Add autoFocus prop
            // autoFocus
            // Add key to force re-render when selection changes
            key={selectedEntities.length}
            style={{
              border: "none",
              outline: "none",
              boxShadow: "none",
              background: "transparent",
              padding: "2px 8px",
              margin: 0,
              width: "100%",
              minWidth: "70px",
              fontSize: "inherit",
              ...props.style,
            }}
            className="no-border no-outline"
          />
        </div>
      );
    },
    [fetchData, selectedEntities, setSelectedEntities, onEntityAdd, displayKey]
  );

  // const focusInput = () => {
  //   if (selectRef.current) {
  //     console.log("[Focus] Attempting to focus select");
  //     selectRef.current.focus();
  //   }
  // };

  // // Add onFocus and onBlur handlers to track focus state
  // const handleFocus = () => {
  //   console.log("[Focus] Select focused");
  // };

  // const handleBlur = () => {
  //   console.log("[Blur] Select blurred");
  // };

  return (
    <div className="col-md-12">
      <div className="form-group form-border">
        <label>
          {`${label}:`}
          {isRequired && <span style={{ color: "red" }}> *</span>}
          {error && <span style={{ color: "red" }}>{error}</span>}
        </label>
        <AsyncSelect
          ref={selectRef}
          cacheOptions
          defaultOptions
          loadOptions={loadOptions}
          isMulti
          value={selectedEntities}
          onChange={handleSelect}
          placeholder={""}
          // onFocus={handleFocus}
          // onBlur={handleBlur}
          isLoading={loading}
          loadingMessage={() => "Loading..."}
          noOptionsMessage={({ inputValue }) =>
            inputValue ? `No results for "${inputValue}"` : emptyRecordMsg
          }
          components={{
            ...animatedComponents,
            DropdownIndicator: null,
            IndicatorSeparator: null,
            ClearIndicator: customCloseIcon,
            Input: CustomInput,
          }}
          styles={{
            control: (base, state) => ({
              ...base,
              // borderColor: error ? "red" : base.borderColor,
              minHeight: "38px",
              // boxShadow: "none",
              // This line disable the blue border
              borderColor: state.isFocused ? "#ccc" : "#e3e3e3",
              boxShadow: state.isFocused ? 0 : 0,
              "&:hover": {
                borderColor: "none",
              },

              "&:focus": {
                borderColor: "none",
              },

              ...styleOverrides.control,
            }),
            multiValue: (base) => ({
              ...base,
              backgroundColor: "#ffedde",
              borderRadius: "30px",
              padding: "0 6px 0 12px",
              marginRight: "2px",
              display: "flex",
              alignContent: "center",
              alignItems: "center",
              whiteSpace: "nowrap", // Prevent text wrapping
              minWidth: "fit-content", // Allow chips to shrink/exp
              height: "30px",
              ":hover": {
                border: "1px solid #ff9b44", // Match your theme
              },
              ...styleOverrides.multiValue,
            }),

            multiValueRemove: (base) => ({
              ...base,
              borderRadius: "50%",
              backgroundColor: "white",

              width: "20px",
              height: "20px",

              transition: "background-color 0.2s",
              ":hover": {
                backgroundColor: "#ff000078", // Match your theme
              },
            }),

            input: (base) => ({
              ...base,
              margin: 0,
              padding: 0,
              flex: 1,
            }),
            valueContainer: (base) => ({
              ...base,
              padding: "2px 8px",
              gap: "8px",
              flexWrap: "wrap",
              display: "flex",
              flex: 1,
            }),

            ...styleOverrides,
          }}
          getOptionLabel={(option) => option[displayKey]}
          getOptionValue={(option) => option.id.toString()}
          // Add these props
          // openMenuOnFocus={true}
          // menuIsOpen={true}
          autoFocus={true}
          // ... rest of the props
        ></AsyncSelect>
      </div>
    </div>
  );
}

export default EntityChips2;
